import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

const HERO_IMG = "/fotos/foto04.jpg";
const HERO_IMG_MOBILE = "/fotos/foto05.jpg";

const lineAnim = (delay) => ({
  initial: { y: "110%" },
  animate: { y: 0 },
  transition: { duration: 0.9, delay, ease: [0.16, 1, 0.3, 1] },
});

const fade = (delay) => ({
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.8, delay, ease: [0.16, 1, 0.3, 1] },
});

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const ghostY = useTransform(scrollYProgress, [0, 1], ["0%", "-40%"]);

  const goTo = (e, href) => {
    e.preventDefault();
    window.__lenis?.scrollTo(href, { offset: -70 });
  };

  return (
    <section ref={ref} data-testid="hero-section" className="relative min-h-screen flex items-end overflow-hidden grain">
      <motion.div style={{ y: bgY }} className="absolute inset-0">
        <img
          src={HERO_IMG}
          alt="Cristian Díaz, volante central de Colo-Colo, rematando en el Estadio Monumental ante la Garra Blanca"
          data-testid="hero-image"
          className="hidden sm:block w-full h-[130%] object-cover object-[72%_62%]"
        />
        <img
          src={HERO_IMG_MOBILE}
          alt="Cristian Díaz con la camiseta de la Selección Chilena Sub-17"
          data-testid="hero-image-mobile"
          className="sm:hidden w-full h-auto [mask-image:linear-gradient(to_bottom,black_55%,transparent_98%)]"
        />
        <div className="absolute inset-0 hero-overlay" />
      </motion.div>

      <motion.span
        style={{ y: ghostY }}
        className="absolute right-[-4%] top-[8%] font-display font-black italic text-[38vw] leading-none text-white/[0.04] select-none pointer-events-none"
        aria-hidden="true"
      >
        42
      </motion.span>

      <div className="relative z-10 w-full max-w-6xl mx-auto px-6 pb-16 pt-40">
        <motion.p {...fade(0.15)} className="font-tech text-xs uppercase tracking-[0.4em] text-[#d4af37] mb-6" data-testid="hero-eyebrow">
          Colo-Colo · Primera División · Chile
        </motion.p>

        <h1 className="font-display font-black uppercase italic leading-[0.85] tracking-tighter text-[17vw] sm:text-[13vw] lg:text-[10rem]" data-testid="hero-title">
          <span className="block overflow-hidden"><motion.span {...lineAnim(0.25)} className="block">Cristian</motion.span></span>
          <span className="block overflow-hidden"><motion.span {...lineAnim(0.4)} className="block text-[#d4af37]">Díaz</motion.span></span>
        </h1>

        <motion.p {...fade(0.7)} className="mt-6 max-w-xl text-base sm:text-lg text-neutral-400 leading-relaxed" data-testid="hero-subtitle">
          Volante central del Cacique. Mundialista Sub-17 con Chile en Qatar 2025
          y debutante profesional en 2026: despliegue, presión y lectura para
          equilibrar el mediocampo.
        </motion.p>

        <motion.div {...fade(0.85)} className="mt-8 flex flex-wrap gap-3" data-testid="hero-stats">
          {[
            ["18", "Años"],
            ["1,73 m", "Estatura"],
            ["N°42", "Volante"],
          ].map(([v, l]) => (
            <div key={l} className="border border-neutral-800 bg-[#0f0f0f]/70 backdrop-blur px-5 py-3">
              <span className="font-display text-2xl font-extrabold" data-testid={`hero-stat-${l.toLowerCase()}`}>{v}</span>
              <span className="block font-tech text-[10px] uppercase tracking-[0.3em] text-neutral-500 mt-1">{l}</span>
            </div>
          ))}
        </motion.div>

        <motion.div {...fade(1)} className="mt-10 flex flex-wrap gap-4">
          <a
            href="#trayectoria"
            data-testid="hero-cta-trayectoria"
            onClick={(e) => goTo(e, "#trayectoria")}
            className="group font-tech text-xs uppercase tracking-[0.25em] bg-white text-black px-8 py-4 hover:bg-[#d4af37] transition-colors"
          >
            Ver Trayectoria →
          </a>
          <a
            href="#contacto"
            data-testid="hero-cta-contacto"
            onClick={(e) => goTo(e, "#contacto")}
            className="font-tech text-xs uppercase tracking-[0.25em] border border-neutral-700 px-8 py-4 hover:border-[#d4af37] hover:text-[#d4af37] transition-colors"
          >
            Contacto Representante
          </a>
        </motion.div>
      </div>
    </section>
  );
}
